import { Plus, Save, X } from "lucide-react";
import type { Notify } from "./admin-types";
import { Header, EmptyState, Outcome } from "./AdminPagePrimitives";

import { AutocompleteSelect } from "../../shared/components/AutocompleteSelect";

import { requestCategoryOptions } from "../procurement/procurement-options";
import { useAdminWorkflowsWorkspace } from "./useAdminWorkflowsWorkspace";

export function WorkflowsPage({notify}: {notify: Notify}) {
  const { saving, rows, composerOpen, setComposerOpen, form, setForm, addStep, createWorkflow } = useAdminWorkflowsWorkspace({notify});
  return (
    <>
      <Header
        eyebrow="Karar yönlendirme"
        title="Onay Akışları"
        description="Tutar, departman ve kategori kriterlerine göre çalışan dinamik onay kurallarını inceleyin."
        action={
          <button
            className="primary-button"
            onClick={() => setComposerOpen(true)}
          >
            <Plus size={15} /> Yeni akış
          </button>
        }
      />
      {composerOpen ? (
        <section className="inline-form-card">
          <div className="section-heading compact">
            <div>
              <span className="section-kicker">Dinamik yönlendirme</span>
              <h2>Onay akışı oluştur</h2>
            </div>
            <button
              className="icon-button"
              aria-label="Onay akışı formunu kapat"
              onClick={() => setComposerOpen(false)}
            >
              <X size={17} />
            </button>
          </div>
          <div className="inline-form-grid">
            <label>
              Akış adı
              <input
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              Öncelik
              <input
                type="number"
                min="1"
                value={form.priority}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    priority: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              Para birimi
              <select
                value={form.currency}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    currency: event.target.value,
                  }))
                }
              >
                <option value="">Tümü</option>
                <option>TRY</option>
                <option>EUR</option>
                <option>USD</option>
              </select>
            </label>
            <label>
              Departman
              <select
                value={form.department}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    department: event.target.value,
                  }))
                }
              >
                <option value="">Tümü</option>
                {[
                  "Planlama",
                  "Üretim",
                  "Lojistik",
                  "Teknik",
                  "Satın Alma",
                  "Genel Müdürlük",
                ].map((department) => (
                  <option key={department}>{department}</option>
                ))}
              </select>
            </label>
            <label>
              En düşük tutar
              <input
                type="number"
                min="0"
                value={form.minimumAmount}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    minimumAmount: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              En yüksek tutar
              <input
                type="number"
                min="0"
                value={form.maximumAmount}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    maximumAmount: event.target.value,
                  }))
                }
              />
            </label>
            <AutocompleteSelect
              className="form-field-wide"
              label="Kategori"
              value={form.category}
              options={requestCategoryOptions}
              allowCustom
              placeholder="Tüm kategoriler veya kategori ara"
              onChange={(category) =>
                setForm((current) => ({ ...current, category }))
              }
            />
            <fieldset className="workflow-step-editor form-field-wide">
              <legend>Onay adımları</legend>
              {form.steps.map((step, index) => (
                <div key={`${index}-${step}`}>
                  <span>{index + 1}</span>
                  <select
                    aria-label={`${index + 1}. onay adımı`}
                    value={step}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        steps: current.steps.map((item, itemIndex) =>
                          itemIndex === index ? event.target.value : item,
                        ),
                      }))
                    }
                  >
                    <option value="SATIN_ALMA">Satın Alma</option>
                    <option value="SATIN_ALMA_MUDURU">Satın Alma Müdürü</option>
                    <option value="GENEL_MUDUR">Genel Müdür</option>
                    <option value="KALITE_UZMANI">Kalite Uzmanı</option>
                    <option value="KALITE_MUDURU">Kalite Müdürü</option>
                    <option value="PLANLAMA">Planlama</option>
                  </select>
                  <button
                    type="button"
                    aria-label={`${index + 1}. adımı kaldır`}
                    disabled={form.steps.length === 1}
                    onClick={() =>
                      setForm((current) => ({
                        ...current,
                        steps: current.steps.filter(
                          (_item, itemIndex) => itemIndex !== index,
                        ),
                      }))
                    }
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              <button
                className="secondary-button"
                type="button"
                onClick={addStep}
              >
                <Plus size={14} /> Adım ekle
              </button>
            </fieldset>
          </div>
          <div className="inline-form-actions">
            <button
              className="secondary-button"
              onClick={() => setComposerOpen(false)}
            >
              Vazgeç
            </button>
            <button className="approve-button" disabled={saving} onClick={createWorkflow}>
              <Save size={15} /> Akışı kaydet
            </button>
          </div>
        </section>
      ) : null}
      <section className="module-card">
        {rows.length ? (
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Akış</th>
                  <th>Öncelik</th>
                  <th>Tutar aralığı</th>
                  <th>Para birimi</th>
                  <th>Adımlar</th>
                  <th>Durum</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <strong>{row.name}</strong>
                    </td>
                    <td>{row.priority}</td>
                    <td>
                      {row.minimum_amount ?? "0"} –{" "}
                      {row.maximum_amount ?? "Sınırsız"}
                    </td>
                    <td>{row.currency ?? "Tümü"}</td>
                    <td>
                      {row.steps?.map((step) => step.role).join(" → ") ?? "—"}
                    </td>
                    <td>
                      <Outcome value={row.active ? "SUCCESS" : "PASSIVE"} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            title="Tanımlı onay akışı bulunmuyor"
            description="Satın alma talepleri onaya gönderilmeden önce en az bir aktif kural tanımlanmalıdır."
          />
        )}
      </section>
    </>
  );
}
